import { FC, useEffect, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { isUndefined, omitBy } from 'lodash-es';
import { InlineNotification, Link, Tag } from '@carbon/react';
import searchAtomPair from 'atoms/search-atom';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  DataTableSelectionState,
  TableState,
} from 'components/data-table/types';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import type { DocumentSearchParams } from 'components/documents-search/types';
import { citationColumns, type CitationColumnId } from './citation-columns';
import {
  EMPTY,
  documentHeading,
  evidenceLabels,
  type CatalogDocument,
  type VestingCandidate,
} from './types';
import styles from './vesting-documents.module.scss';

// One catalog, not two lists. The finder's candidates rank to the top of the corpus search with
// the evidence behind their Match cell; everything else the tract's county holds follows below.
//
// The alternative — a "suggestions" tab beside a "search" tab — makes "the matcher found nothing"
// a different screen from "let me look myself", when they are the same question. It also turns an
// empty Elasticsearch index into a dead tab rather than one degraded column.

// Descending, against the app's ascending habit, because the mock and the story fix this
// order; 100 is DEFAULT_PAGE_SIZE and the corpus is browsed, so a shorter page means more paging.
const CATALOG_PAGE_SIZES = [100, 50, 25];

/** The documents query the CRM document page reads, across every document type. */
const CORPUS_HREF = '/queries/documents';

interface CorpusSearchParams extends DocumentSearchParams {
  conveyor?: string;
  conveyee?: string;
  conveyeeIds?: number[];
  minInstrumentDate?: string;
  maxInstrumentDate?: string;
  minFileDate?: string;
  maxFileDate?: string;
  minRecordedDate?: string;
  maxRecordedDate?: string;
  documentSubtypes?: string[];
  documentTypeNames?: string[];
  /** STI types by index key, e.g. 'courthouse'. */
  documentTypes?: string[];
  stateIds?: number[];
}

/** A removable pre-fill: what the chip says, and the search it narrows to. */
export interface CatalogFilter {
  key: string;
  label: string;
  hint: string;
  params: Pick<
    CorpusSearchParams,
    'withinReferenceLocationId' | 'conveyeeIds' | 'countyIds' | 'documentTypes'
  >;
}

// The documents query sorts these on the server; the other citation columns it cannot.
const SORT_ATTRIBUTES: Partial<Record<CitationColumnId, string>> = {
  documentType: 'document_type',
  instrumentDate: 'instrument_date',
  fileDate: 'file_date',
  recordedDate: 'recorded_date',
};

const dateRange =
  (min: keyof CorpusSearchParams, max: keyof CorpusSearchParams) =>
  (value: unknown) => {
    const [from, to] = (value ?? []) as [string | null, string | null];
    return { [min]: from ?? undefined, [max]: to ?? undefined };
  };

// The documents query filters these on the server; volume, page and instrument number it cannot.
const FILTER_PARAMS: Partial<
  Record<CitationColumnId, (value: unknown) => Partial<CorpusSearchParams>>
> = {
  conveyors: (value) => ({ conveyor: value as string }),
  conveyees: (value) => ({ conveyee: value as string }),
  instrumentDate: dateRange('minInstrumentDate', 'maxInstrumentDate'),
  fileDate: dateRange('minFileDate', 'maxFileDate'),
  recordedDate: dateRange('minRecordedDate', 'maxRecordedDate'),
  documentType: (value) => ({ documentTypeNames: value as string[] }),
  bookType: (value) => ({ documentSubtypes: value as string[] }),
  countyName: (value) => ({ countyIds: (value as string[]).map(Number) }),
  stateName: (value) => ({ stateIds: (value as string[]).map(Number) }),
};

type CorpusFacets = Partial<
  Record<
    'county' | 'state' | 'documentSubtype' | 'documentTypeName',
    { buckets: { id: string | number; name: string }[] } | null
  >
>;

// Enum filters offer the facet the same search returns.
const FACETS: Partial<Record<CitationColumnId, keyof CorpusFacets>> = {
  documentType: 'documentTypeName',
  bookType: 'documentSubtype',
  countyName: 'county',
  stateName: 'state',
};

/** Both sources meet as the runsheet's own field names, so the runsheet's columns render either. */
export interface CatalogRow extends Record<string, unknown> {
  documentId: number;
  href: string;
  documentType: string;
  conveyors: string[];
  conveyees: string[];
  instrumentDate: string | null;
  fileDate: string | null;
  recordedDate: string | null;
  bookType: string | null;
  volume: string | null;
  page: string | null;
  instrumentNumber: string | null;
  countyName: string;
  stateName: string | null;
  /** null for a plain corpus row — the finder did not rank it. */
  score: number | null;
  reasons: string[];
}

const catalogRow = (
  document: CatalogDocument,
  candidate?: VestingCandidate
): CatalogRow => ({
  documentId: document.id,
  href: document.href,
  documentType: documentHeading(document.documentType),
  conveyors: document.partySummary?.grantors?.map((party) => party.name) ?? [],
  conveyees: document.partySummary?.grantees?.map((party) => party.name) ?? [],
  instrumentDate: document.instrumentDate ?? null,
  fileDate: document.fileDate ?? null,
  recordedDate: document.recordedDate ?? null,
  bookType: document.documentSubtype ?? null,
  volume: document.volume ?? null,
  page: document.page ?? null,
  instrumentNumber: document.instrumentNumber ?? null,
  countyName: document.county?.name || EMPTY,
  stateName: document.stateAbbreviation ?? null,
  score: candidate
    ? (candidate.score ?? candidate.reason?.score ?? null)
    : null,
  reasons: evidenceLabels(
    candidate?.reason?.evidence,
    candidate?.reason?.interestTypeMatch
  ),
});

const candidateRow = (candidate: VestingCandidate) =>
  catalogRow(candidate.document, candidate);

const NO_CANDIDATES: VestingCandidate[] = [];

interface Props {
  /** Ranked to the top of page one. Few enough that they never need paging of their own. Omitted, nothing ranks. */
  candidates?: VestingCandidate[];
  candidatesLoading?: boolean;
  /** Removable pre-fills, looser than the matcher's constraints so the search can surface what it ruled out. */
  filters?: CatalogFilter[];
  /** Seeds the search text, e.g. the recording reference of the row being linked. */
  initialText?: string;
  /** Checkboxes instead of a single highlighted row. */
  multiple?: boolean;
  selectedDocumentIds: number[];
  onSelect: (documentIds: number[]) => void;
}

export const DocumentCatalog: FC<Props> = ({
  candidates: ranked,
  candidatesLoading = false,
  filters = [],
  initialText = '',
  multiple = false,
  selectedDocumentIds,
  onSelect,
}) => {
  const candidates = ranked ?? NO_CANDIDATES;
  // A fresh atom pair per mount so reopening the panel starts clean.
  const { searchAtom, resetSearchDataAtom } = useMemo(
    () =>
      searchAtomPair<CatalogDocument, CorpusSearchParams, CorpusFacets>({
        href: CORPUS_HREF,
      }),
    []
  );
  const [searchState, runSearch] = useAtom(searchAtom);
  const resetSearch = useSetAtom(resetSearchDataAtom);

  const [text, setText] = useState(initialText);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const columnParams: Partial<CorpusSearchParams> = omitBy(
    Object.assign(
      {},
      ...columnFilters.map(({ id, value }) =>
        FILTER_PARAMS[id as CitationColumnId]?.(value)
      )
    ),
    isUndefined
  );
  // A column's county replaces the county chip's, so that chip no longer scopes and is not shown.
  const scoping = filters.filter(
    (filter) =>
      !removed.includes(filter.key) &&
      Object.keys(filter.params).every((key) => !(key in columnParams))
  );
  const params: Partial<CorpusSearchParams> = Object.assign(
    {},
    ...scoping.map((filter) => filter.params),
    columnParams
  );
  // By value: callers rebuild `filters` on every render, and each new search re-renders them.
  const paramsKey = JSON.stringify(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterParams = useMemo(() => params, [paramsKey]);

  useEffect(() => {
    const [sort] = sorting;
    runSearch(() => ({
      text,
      page,
      pageSize,
      paginated: true,
      ...filterParams,
      ...(sort && {
        sortAttribute: SORT_ATTRIBUTES[sort.id as CitationColumnId],
        sortDirection: sort.desc ? 'DESC' : 'ASC',
      }),
    }));
  }, [runSearch, text, page, pageSize, filterParams, sorting]);

  useEffect(
    () => () => resetSearch({ unsetCurrentPayload: true }),
    [resetSearch]
  );

  // Candidates pin while only sorting. A filter or search text asks a different question, so a
  // candidate then appears only where the corpus lists it, still marked.
  const pinned = !text.trim() && columnFilters.length === 0;
  const candidatesById = useMemo(
    () =>
      new Map(candidates.map((candidate) => [candidate.documentId, candidate])),
    [candidates]
  );

  const rows = useMemo(() => {
    const corpus = (searchState.data?.results ?? []).map((document) =>
      catalogRow(document, candidatesById.get(document.id))
    );
    if (!pinned) return corpus;

    // A candidate already sits at the top; showing it twice reads as two documents.
    const unranked = corpus.filter(
      (row) => !candidatesById.has(row.documentId)
    );
    // Candidates head page one only. Repeating them on every page would put the same three
    // deeds in front of someone who has already passed over them.
    return page === 1
      ? [...candidates.map(candidateRow), ...unranked]
      : unranked;
  }, [searchState.data, candidatesById, candidates, pinned, page]);

  // A failed corpus search contributes nothing — neither rows nor a count. Adding its stale or
  // absent total would claim documents the list cannot show.
  const corpusFailed = !!searchState.error;
  const corpusResults = corpusFailed ? [] : (searchState.data?.results ?? []);
  const corpusTotal = corpusFailed ? 0 : (searchState.data?.totalCount ?? 0);
  // Pinned candidates outside the corpus add to its total, knowable only when page one holds it all.
  const corpusIds = new Set(corpusResults.map((row) => row.id));
  const wholeCorpus =
    pinned && page === 1 && corpusResults.length >= corpusTotal;
  const totalCount =
    corpusTotal +
    (wholeCorpus
      ? candidates.filter((candidate) => !corpusIds.has(candidate.documentId))
          .length
      : 0);

  const facets = searchState.data?.facets;
  const columns = useMemo<DataTableColumnDef<CatalogRow>[]>(
    () => [
      {
        id: 'match',
        header: 'Match',
        size: 140,
        enableSorting: false,
        meta: { label: 'Match', toggleColumnFilter: false },
        cell: ({ row }) => {
          // An em dash, not an empty cell: the column means "the finder had nothing to say
          // about this document", which is different from a value that failed to load.
          return row.original.score === null ? (
            <span className={styles.plainMatch}>{EMPTY}</span>
          ) : (
            <Tag type="teal" size="sm">
              Candidate
            </Tag>
          );
        },
      },
      ...citationColumns<CatalogRow>().map((column) => {
        const { id } = column;
        const facet = FACETS[id];
        return {
          ...column,
          enableSorting: !!SORT_ATTRIBUTES[id],
          enableColumnFilter: !!FILTER_PARAMS[id],
          ...(facet && {
            meta: {
              ...column.meta,
              type: 'enum' as const,
              filterOptions: (facets?.[facet]?.buckets ?? []).map((bucket) => ({
                value: String(bucket.id),
                label: bucket.name,
              })),
            },
          }),
          ...(id === 'documentType' && {
            cell: ({ row }: { row: { original: CatalogRow } }) => (
              <Link
                href={row.original.href}
                target="_blank"
                rel="noreferrer"
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
              >
                {row.original.documentType}
              </Link>
            ),
          }),
        };
      }),
    ],
    [facets]
  );

  const selection = useMemo<DataTableSelectionState>(
    () => ({
      selectAll: false,
      toggledNodes: selectedDocumentIds.map(String),
    }),
    [selectedDocumentIds]
  );

  return (
    <div className={styles.catalog}>
      {scoping.length > 0 && (
        <div className={styles.scopeRow}>
          <span className={styles.scopeLabel}>Scoped to</span>
          {scoping.map((filter) => (
            <Tag
              key={filter.key}
              type="gray"
              size="sm"
              filter
              onClose={() => {
                setRemoved((current) => [...current, filter.key]);
                setPage(1);
              }}
              title={`Remove ${filter.hint}`}
            >
              {filter.label}
            </Tag>
          ))}
          <span className={styles.scopeHint}>
            Remove a filter to search more of the corpus.
          </span>
        </div>
      )}

      {ranked && pinned && (
        <p className={styles.catalogSummary}>
          {candidatesLoading
            ? 'Looking for candidates…'
            : `${candidates.length} ranked to the top by grantee evidence. Everything else follows.`}
        </p>
      )}

      {corpusFailed && rows.length > 0 && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Only the suggested documents are listed"
          subtitle="The document catalog could not be searched, so browsing the rest of the corpus is unavailable."
        />
      )}

      <DataTable<CatalogRow>
        data={rows}
        columns={columns}
        getRowId={(row) => String(row.documentId)}
        isLoading={searchState.loading || candidatesLoading}
        // DataTable replaces its whole body with the error, so handing it one would hide the
        // finder's candidates — which came from a different endpoint and are still good answers.
        // Only let it take over when there is genuinely nothing else to show.
        error={rows.length === 0 ? (searchState.error ?? null) : null}
        searchPlaceholder="Search all documents — instrument, recording, party…"
        scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
        pageSizes={CATALOG_PAGE_SIZES}
        controlledState={{
          globalFilter: text,
          pagination: { page, pageSize },
          selection,
          sorting,
          columnFilters,
        }}
        onSortChange={(state: TableState) => {
          setSorting(state.sorting);
          setPage(1);
        }}
        onFilterChange={(state: TableState) => {
          setColumnFilters(state.columnFilters);
          setPage(1);
        }}
        onGlobalSearchChange={(state: TableState) => {
          setText(state.globalFilter);
          setPage(1);
        }}
        onPaginationChange={(state: TableState) => {
          setPage(state.page);
          setPageSize(state.pageSize);
        }}
        onSelectionChange={(state: TableState) => {
          onSelect(state.selection.toggledNodes.map(Number));
        }}
        // Only a ranked row has anything to disclose; a plain corpus row would open an empty panel.
        getRowCanExpand={(row) => row.original.score !== null}
        renderRowSubComponent={(row) => (
          <div className={styles.evidencePanel}>
            <div className={styles.evidenceHead}>
              <span className={styles.evidenceHeading}>
                Why the finder ranked this
              </span>
              <Tag
                type="gray"
                size="sm"
              >{`Score ${row.original.score} of 4`}</Tag>
            </div>
            <ul className={styles.evidenceList}>
              {row.original.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
        config={{
          selectionType: multiple ? 'multi' : 'single',
          expandAllToggle: true,
          columnFilters: true,
          sizeToContent: true,
          columnVisibility: false,
          columnReordering: false,
          columnResizing: false,
          columnPinning: false,
        }}
      />
    </div>
  );
};
